import { LegalPageShell } from '@/components/public/legal-page-shell';
import { TERMS_OF_SERVICE_VERSION } from '@/constants/legal';

/**
 * TermsPage — public, unauthenticated route at `/terms`.
 *
 * Linked from:
 *  - login.tsx footer ("By signing in you agree to our Terms of Service")
 *  - ActivationPage consent step
 *  - ResidentSelfRegistrationPage consent step
 *
 * Reviewed and approved by Secure Labs co-founders, 5 May 2026, in
 * lieu of external counsel sign-off until the first paying estate is
 * onboarded. A counsel review is scheduled before commercial launch and
 * any redlines they propose will be bumped via TERMS_OF_SERVICE_VERSION.
 *
 * Key drafting decisions:
 *   - Liability cap (§11): greater of 12 months' fees or KES 1,000,000, with
 *     KDPA §56 statutory damages explicitly carved out as non-waivable.
 *   - Access decisions (§3): Platform supports but does not make gate/entry
 *     decisions — responsibility rests with the Estate Operator.
 *   - Breach notification (§17): standalone clause, 72-hour ODPC notification
 *     + without-undue-delay data subject notification per KDPA §43.
 *   - Dispute resolution (§14): negotiation -> mediation in Nairobi (NCIA
 *     rules) -> exclusive jurisdiction of the courts of Kenya; individual
 *     residential users may go directly to court without the mediation step.
 *   - Indemnity (§12): limited to users acting in a commercial or professional
 *     capacity; does not apply to individual residential users.
 */
export function TermsPage() {
  return (
    <LegalPageShell
      title="Terms of Service"
      effectiveDate="5 May 2026"
      version={TERMS_OF_SERVICE_VERSION}
    >
      <section>
        <h2 className="text-xl">Contents</h2>
        <ol className="ml-6 list-decimal space-y-1 text-sm">
          <li>
            <a className="hover:underline" href="#terms-1">
              Definitions
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-2">
              Acceptance of these terms
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-3">
              The service
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-4">
              Your account
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-5">
              Acceptable use
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-6">
              Visitor data and consent
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-7">
              Estate Operator relationship
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-8">
              Service availability
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-9">
              Intellectual property
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-10">
              Termination
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-11">
              Limitation of liability
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-12">
              Indemnity
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-13">
              Changes to these terms
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-14">
              Governing law and dispute resolution
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-15">
              Severability
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-16">
              Entire agreement
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-17">
              Breach notification
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-18">
              Force majeure
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-19">
              Notices
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-20">
              Assignment
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-21">
              No third-party beneficiaries
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-22">
              Waiver
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-23">
              Anti-corruption
            </a>
          </li>
          <li>
            <a className="hover:underline" href="#terms-24">
              Contact and version history
            </a>
          </li>
        </ol>
      </section>

      <section id="terms-1" className="scroll-mt-24">
        <h2 className="text-xl">1. Definitions</h2>
        <p>In these Terms:</p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            <strong>Platform</strong> means the Secure Labs visitor and access management software
            provided by Secure Labs Limited.
          </li>
          <li>
            <strong>We</strong>, <strong>us</strong>, <strong>our</strong> refer to Secure Labs
            Limited.
          </li>
          <li>
            <strong>You</strong>, <strong>user</strong> means any individual who accesses or uses
            the Platform.
          </li>
          <li>
            <strong>Estate Operator</strong> means the residential estate, commercial property, or
            master-planned development that has subscribed to the Platform.
          </li>
          <li>
            <strong>Privacy Policy</strong> means the Secure Labs Privacy Policy published at{' '}
            <a className="underline" href="/privacy">
              /privacy
            </a>
            , as amended from time to time.
          </li>
          <li>
            <strong>KDPA</strong> means the Kenya Data Protection Act 2019 (Act No. 24 of 2019).
          </li>
          <li>
            <strong>Business day</strong> means a day (other than a Saturday, Sunday, or a public
            holiday in Kenya) on which banks are open for general business in Nairobi.
          </li>
          <li>
            <strong>Enterprise Tenant</strong> means a company or organisation that occupies part of
            an Estate Operator&apos;s property and whose administrator manages employee access
            through the Platform under the Estate Operator&apos;s subscription.
          </li>
        </ul>
        <p>
          These Terms govern your use of the Platform only. They do not create any employment,
          tenancy, or other relationship between you and an Estate Operator, an Enterprise Tenant,
          or Secure Labs Limited beyond what is expressly set out here.
        </p>
      </section>

      <section id="terms-2" className="scroll-mt-24">
        <h2 className="text-xl">2. Acceptance of these terms</h2>
        <p>
          These Terms of Service (the &ldquo;Terms&rdquo;) govern your access to and use of the
          Platform. By creating an account, activating an account, or otherwise using the Platform,
          you confirm that you accept these Terms and the Privacy Policy. Your acceptance is
          recorded in our consent log together with the version of these Terms you accepted, the
          date and time of acceptance, your IP address, and your browser type and version (KDPA
          §32(4)).
        </p>
        <p>
          By creating or activating an account, you confirm that you are at least eighteen (18)
          years old. This confirmation is recorded as part of your account identity data. If you are
          not at least eighteen, you must not use the Platform.
        </p>
        <p>
          If you are using the Platform on behalf of another person or organisation, you represent
          that you are authorised to bind that person or organisation to these Terms and that you
          accept these Terms on their behalf.
        </p>
      </section>

      <section id="terms-3" className="scroll-mt-24">
        <h2 className="text-xl">3. The service</h2>
        <p>
          The Platform provides visitor and access management services to Estate Operators. The
          features available to you depend on (i) your role on the Platform and (ii) the modules
          that the Estate Operator has enabled. We may add, modify, or remove features from time to
          time. Where a change materially reduces the functionality you previously had, we will
          notify you in advance.
        </p>
        <p>
          Beta or experimental features may be made available from time to time. They are clearly
          marked as such and are provided without representation or warranty and without any service
          commitment.
        </p>
        <p>
          Certain notifications — including visit confirmations, access alerts, account and security
          notices — are part of the Platform&apos;s core functionality and will continue while your
          account is active. You can only stop receiving these by closing your account. Optional
          marketing communications, if we introduce them, will require your prior consent and will
          include a clear opt-out.
        </p>
        <p>
          The Platform supports access-control decisions made by Estate Operators and their staff,
          but it does not itself decide who may enter or remain on any premises. The final decision
          to admit or deny entry to a visitor always rests with the Estate Operator and its
          authorised personnel, not with Secure Labs Limited.
        </p>
      </section>

      <section id="terms-4" className="scroll-mt-24">
        <h2 className="text-xl">4. Your account</h2>
        <p>
          You are responsible for maintaining the confidentiality of your account credentials,
          including your password and any multi-factor authentication codes. You agree to notify us
          immediately of any unauthorised use of your account by contacting{' '}
          <a className="underline" href="mailto:privacy@securelabsystems.com">
            privacy@securelabsystems.com
          </a>
          . You may not share your account or transfer it to another person.
        </p>
        <p>
          You may delete your account at any time via Account Settings. On deletion, the data we
          retain and the data we anonymise are described in §10 of the Privacy Policy.
        </p>
        <p>
          We may suspend or terminate your account if you breach these Terms, if your use of the
          Platform creates a security risk for other users, if your Estate Operator instructs us to
          do so (in respect of accounts associated with that Estate Operator), or if we are required
          to do so by law. Where the suspension is not urgent we will give you reasonable notice and
          a chance to cure the breach. We may also suspend or restrict access to the Platform, or to
          particular features, where reasonably necessary for maintenance, security, or legal
          compliance.
        </p>
      </section>

      <section id="terms-5" className="scroll-mt-24">
        <h2 className="text-xl">5. Acceptable use</h2>
        <p>You agree that you will not:</p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Use the Platform to harass, threaten, defame, or stalk any person.</li>
          <li>
            Submit personal data of any third party without their lawful permission or other lawful
            basis.
          </li>
          <li>
            Attempt to bypass the Platform&apos;s authentication, rate limiting, tenant-isolation,
            or any other security control.
          </li>
          <li>
            Use the Platform to circumvent the access-control rules of an Estate Operator (for
            example, to admit a visitor whose entry the Estate Operator has not authorised).
          </li>
          <li>
            Impersonate any other person or misrepresent your affiliation with any person or entity.
          </li>
          <li>Scrape, crawl, or extract data from the Platform in bulk by any automated means.</li>
          <li>Use the Platform to send unsolicited commercial communications.</li>
          <li>
            Reverse engineer, decompile, or otherwise attempt to derive source code from the
            Platform, except to the limited extent that applicable law allows such activities
            notwithstanding a contractual prohibition.
          </li>
          <li>
            Use the Platform in any manner that violates the Computer Misuse and Cybercrimes Act
            2018, the KDPA, or any other applicable law.
          </li>
        </ul>
        <p>
          If we reasonably believe that your use of the Platform breaches this section or creates a
          risk to other users, to an Estate Operator, or to the security or integrity of the
          Platform, we may investigate and take appropriate action, including suspension or
          termination of your account.
        </p>
      </section>

      <section id="terms-6" className="scroll-mt-24">
        <h2 className="text-xl">6. Visitor data and consent</h2>
        <p>
          When you invite a visitor or add a contact to your address book, you confirm that you have
          a lawful basis to share that person&apos;s contact details with the Platform — typically
          because you have asked them and they have consented. Where the visitor is your invitee,
          you are the data controller for the personal data of that visitor that you submit, and we
          act as your data processor in respect of that submission. Where your visitor pre-registers
          via SMS reply, we use the phone number already associated with the invitation; that
          processing is described in §15 of the Privacy Policy and relies on the same lawful-basis
          allocation set out in this section. Where you are the inviting resident, you should ensure
          that the visitor is aware that their personal data will be shared with the Platform for
          access-management purposes; the Estate Operator&apos;s privacy notice or a disclosure from
          you as the inviting resident typically fulfils this requirement.
        </p>
        <p>
          Where an Estate Operator or Enterprise Tenant uses the Platform, that organisation is an
          independent data controller for its own employment, tenancy, and visitor-management
          purposes. It is responsible for having a lawful basis to process personal data through the
          Platform and for providing its own privacy notice and obtaining any required consents.
        </p>
        <p>
          The visitor has rights under the KDPA — including access, rectification, and erasure —
          that they can exercise by contacting the Estate Operator they visited or by emailing us at{' '}
          <a className="underline" href="mailto:privacy@securelabsystems.com">
            privacy@securelabsystems.com
          </a>
          . You agree to cooperate with any reasonable request from us to enable a visitor to
          exercise those rights, including by providing context about your purpose for processing
          their data. The scope of these rights and how to exercise them — including access,
          rectification, erasure, restriction, portability, and objection — is described in §11 of
          the Privacy Policy.
        </p>
        <p>
          Where you submit data relating to a child, you are responsible for ensuring that doing so
          is lawful and, where required, that appropriate consent from a parent or guardian has been
          obtained in accordance with the KDPA and the Children Act 2022. We process such data only
          for access-control and security-incident purposes and do not use children&apos;s data for
          marketing or profiling.
        </p>
        <p>
          We also process certain data about your use of the Platform — such as access logs,
          security events, and rate-limit records — as an independent data controller for the
          purposes described in the Privacy Policy. Our process and timelines for handling data
          subject requests in respect of that data are described in the{' '}
          <a className="underline" href="/privacy">
            Privacy Policy
          </a>
          .
        </p>
      </section>

      <section id="terms-7" className="scroll-mt-24">
        <h2 className="text-xl">7. Estate Operator relationship</h2>
        <p>
          Where you are a resident, employee, or visitor of an Estate Operator, the Estate Operator
          is the data controller for the access-control purposes carried out at its premises and we
          act as the Estate Operator&apos;s data processor. Our processing in that capacity is
          governed by a Data Processing Agreement between us and the Estate Operator. Data relating
          to one Estate Operator or Enterprise Tenant is not accessible to another through the
          Platform.
        </p>
        <p>
          You should be aware that the Estate Operator&apos;s property manager and (where
          applicable) security supervisor can view records of your visitors and access events on the
          Platform. The Estate Operator&apos;s own privacy notice governs its use of that visitor
          and access data; Secure Labs&apos;s Privacy Policy does not cover processing carried out
          by the Estate Operator in its capacity as an independent data controller.
        </p>
        <p>
          Where you are an employee or contractor of an Enterprise Tenant, your employer is the data
          controller for visitor and access data within its zone, and we act as the company&apos;s
          data processor. Your employer&apos;s own privacy notice governs the employment aspects of
          your data use on the Platform. Your employer is responsible for having a lawful basis to
          onboard you as a Platform user.
        </p>
        <p>
          The allocation of data-controller responsibilities between us, the Estate Operator, and
          any Enterprise Tenant is further described in the Privacy Policy.
        </p>
      </section>

      <section id="terms-8" className="scroll-mt-24">
        <h2 className="text-xl">8. Service availability</h2>
        <p>
          We target high availability and take reasonable steps to minimise downtime. We do not
          guarantee uninterrupted service. Where the Platform is temporarily unavailable, our
          gate-side guard application is designed to operate offline for a defined trust window so
          that physical access at the gate is not blocked.
        </p>
        <p>
          We may schedule planned maintenance windows in advance and will give Estate Operators
          reasonable notice of any maintenance that is likely to affect Platform availability.
        </p>
        <p>
          From time to time, the Platform may be unavailable due to factors outside our reasonable
          control (including those described in §18). To the fullest extent permitted by law and
          subject to §11, we are not liable for any loss or damage arising solely from temporary
          unavailability of the Platform.
        </p>
        <p>
          Subject to §11, the Platform is provided &ldquo;as is&rdquo; and &ldquo;as
          available&rdquo;. We make no express or implied warranty, including any implied warranty
          of merchantability, fitness for a particular purpose, or non-infringement, to the fullest
          extent permitted by applicable Kenyan law.
        </p>
      </section>

      <section id="terms-9" className="scroll-mt-24">
        <h2 className="text-xl">9. Intellectual property</h2>
        <p>
          The Platform, including its software, design, branding, and content, is the intellectual
          property of Secure Labs Limited or its licensors. You receive a limited, non-exclusive,
          non-transferable, revocable licence to use the Platform for its intended purpose. No other
          rights are granted.
        </p>
        <p>
          You retain ownership of any content you submit to the Platform (for example, visitor names
          you enter when issuing an invitation). By submitting that content, you grant us a
          worldwide, royalty-free, non-exclusive licence to host, store, transmit, and display the
          content for the limited purpose of operating the Platform on your behalf and providing the
          service to you and to the Estate Operator.
        </p>
        <p>
          If you choose to send us feedback or suggestions, you agree that we may use that feedback
          without obligation to you.
        </p>
      </section>

      <section id="terms-10" className="scroll-mt-24">
        <h2 className="text-xl">10. Termination</h2>
        <p>
          You may delete your account at any time via Account Settings. We may suspend or terminate
          your access if you breach these Terms or if we are required to do so by law. Upon
          termination, the rights granted to you under these Terms cease immediately.
        </p>
        <p>
          Where an Estate Operator cancels its subscription to the Platform, accounts associated
          with that Estate Operator will be deactivated. We will use reasonable efforts to give
          users associated with that Estate Operator at least fourteen (14) days&apos; notice of
          deactivation, except where the cancellation is immediate as a result of non-payment,
          breach of contract, or an instruction from a regulatory or law-enforcement authority. Data
          retained after deactivation is handled in accordance with §10 of the Privacy Policy.
        </p>
        <p>
          The following sections survive termination: §1 (Definitions), §6 (Visitor data and consent
          — in respect of data submitted before termination), §7 (Estate Operator relationship — in
          respect of data processed before termination), §9 (Intellectual property), §11 (Limitation
          of liability), §12 (Indemnity), §14 (Governing law and dispute resolution), §15
          (Severability), §16 (Entire agreement), §17 (Breach notification), §18 (Force majeure),
          §19 (Notices), §20 (Assignment), §21 (No third-party beneficiaries), §22 (Waiver), and §23
          (Anti-corruption), together with the data-retention obligations described in §10 of the
          Privacy Policy, including any legal-hold extension that applies where data is subject to a
          live regulatory investigation or legal proceeding.
        </p>
        <p>
          Your statutory rights under the KDPA in respect of personal data we continue to hold after
          termination are described in the Privacy Policy and survive termination of these Terms.
        </p>
        <p>
          We are not obliged to refund any sum paid, if any, in respect of any period before
          termination.
        </p>
      </section>

      <section id="terms-11" className="scroll-mt-24">
        <h2 className="text-xl">11. Limitation of liability</h2>
        <p>
          Subject to the carve-outs below, our total aggregate liability to you in connection with
          the Platform — whether in contract, tort (including negligence), or otherwise — arising
          out of or in connection with these Terms or your use of the Platform is limited to the
          greater of (a) the total fees paid by you (if any) in the twelve (12) months preceding the
          event giving rise to the claim, or (b) Kenya Shillings One Million (KES 1,000,000).
        </p>
        <p>
          We are not liable for any indirect, incidental, special, consequential, exemplary, or
          punitive damages, or for loss of profits, revenue, data, or business opportunity, even if
          we have been advised of the possibility of such damages, and even if any limited remedy
          fails of its essential purpose, to the extent that such exclusion is permitted under
          applicable law.
        </p>
        <p>
          Nothing in these Terms limits or excludes our liability for (i) death or personal injury
          caused by our negligence, (ii) fraud or fraudulent misrepresentation, (iii) wilful
          misconduct, (iv) any liability that cannot be limited or excluded under applicable Kenyan
          law, or (v) any award of damages made under section 56 of the Kenya Data Protection Act
          2019, which we acknowledge is non-waivable.
        </p>
      </section>

      <section id="terms-12" className="scroll-mt-24">
        <h2 className="text-xl">12. Indemnity</h2>
        <p>
          You agree to indemnify Secure Labs Limited against direct losses, damages, costs, and
          expenses (including reasonable legal fees) to the extent arising out of (i) your unlawful
          submission of a third party&apos;s personal data to the Platform, (ii) your breach of
          these Terms, or (iii) your wilful violation of any applicable law in connection with your
          use of the Platform. Your aggregate indemnification liability under this section is
          subject to the same cap set out in §11. This indemnity does not apply to indirect or
          consequential losses. This indemnity applies only where you are using the Platform in a
          commercial or professional capacity, including as an Estate Operator administrator or
          Enterprise Tenant administrator; it does not apply to individual residential users of the
          Platform. Nothing in this section limits or affects any rights you have as a data subject
          under the KDPA or any other non-waivable applicable law.
        </p>
      </section>

      <section id="terms-13" className="scroll-mt-24">
        <h2 className="text-xl">13. Changes to these terms</h2>
        <p>
          We may update these Terms from time to time. Where the change is material, we will give
          you not less than thirty (30) days&apos; advance notice by email and by an in-app banner,
          and we will require your re-acceptance on next sign-in after the new Terms take effect. If
          you do not accept the new Terms, you may delete your account at any time before they take
          effect; continued use of the Platform after the effective date constitutes acceptance.
        </p>
      </section>

      <section id="terms-14" className="scroll-mt-24">
        <h2 className="text-xl">14. Governing law and dispute resolution</h2>
        <p>
          These Terms are governed by the laws of Kenya. Nothing in these Terms limits any rights
          you may have as a consumer under the Kenya Consumer Protection Act 2012 or any other
          applicable consumer protection law.
        </p>
        <p>
          The parties shall first attempt to resolve any dispute by good-faith negotiation. If the
          dispute is not resolved within thirty (30) days of written notice from one party to the
          other, either party may refer the dispute to mediation in Nairobi under the mediation
          rules of the Nairobi Centre for International Arbitration (NCIA). If mediation fails to
          resolve the dispute within sixty (60) days of the appointment of the mediator, the courts
          of Kenya shall have exclusive jurisdiction. Nothing in this section prevents either party
          from seeking urgent injunctive relief from a court.
        </p>
        <p>
          Notwithstanding the above, an individual user who is not using the Platform in a
          commercial or professional capacity may refer any dispute directly to the courts of Kenya
          without first completing the negotiation or mediation steps set out above.
        </p>
      </section>

      <section id="terms-15" className="scroll-mt-24">
        <h2 className="text-xl">15. Severability</h2>
        <p>
          If any provision of these Terms is held by a court of competent jurisdiction to be
          invalid, illegal, or unenforceable, that provision shall be severed and the remaining
          provisions shall continue in full force. The parties shall in good faith negotiate a
          replacement provision that achieves, to the maximum extent possible, the original
          commercial intent.
        </p>
      </section>

      <section id="terms-16" className="scroll-mt-24">
        <h2 className="text-xl">16. Entire agreement</h2>
        <p>
          These Terms, together with the Privacy Policy and any agreement between us and your Estate
          Operator that applies to your use of the Platform, constitute the entire agreement between
          you and us in respect of the Platform. They supersede any prior or contemporaneous
          understandings, communications, or agreements, whether oral or written.
        </p>
        <p>
          If there is any conflict between these Terms and a separate written agreement between us
          and an Estate Operator or Enterprise Tenant, that separate agreement will govern our
          relationship with that Estate Operator or Enterprise Tenant to the extent of the conflict,
          and these Terms will govern our relationship with you as a user.
        </p>
      </section>

      <section id="terms-17" className="scroll-mt-24">
        <h2 className="text-xl">17. Breach notification</h2>
        <p>
          In the event of a personal-data breach that is likely to result in a risk to your rights
          and freedoms, we will notify the Office of the Data Protection Commissioner (ODPC) within
          seventy-two (72) hours of becoming aware of the breach where required by KDPA §43, and we
          will notify affected data subjects without undue delay.
        </p>
      </section>

      <section id="terms-18" className="scroll-mt-24">
        <h2 className="text-xl">18. Force majeure</h2>
        <p>
          Neither party is liable for any failure or delay in performance to the extent that the
          failure or delay is caused by an event beyond that party&apos;s reasonable control,
          including (without limitation) acts of God, natural disaster, civil unrest, war,
          terrorism, government action, sustained nationwide internet outage lasting more than
          twenty-four (24) hours, sustained power-grid failure, or epidemic. The affected party
          shall notify the other promptly and use reasonable efforts to mitigate the effect of the
          event.
        </p>
      </section>

      <section id="terms-19" className="scroll-mt-24">
        <h2 className="text-xl">19. Notices</h2>
        <p>
          Notices to us must be sent by email to{' '}
          <a className="underline" href="mailto:privacy@securelabsystems.com">
            privacy@securelabsystems.com
          </a>
          . Notices to you may be sent by email to the address associated with your account, by
          in-app banner, or by any other means we reasonably determine to be likely to reach you. A
          notice is deemed received on the day it is sent if sent on a business day before 17:00
          EAT, and otherwise on the next business day.
        </p>
      </section>

      <section id="terms-20" className="scroll-mt-24">
        <h2 className="text-xl">20. Assignment</h2>
        <p>
          You may not assign or transfer your rights or obligations under these Terms without our
          prior written consent. We may assign or transfer our rights and obligations to an
          affiliate, to a successor in interest in connection with a merger, acquisition, or sale of
          all or substantially all of our assets, or to any party that we engage to operate the
          Platform on our behalf, in each case without your consent. Where we assign these Terms to
          a new entity in a merger, acquisition, or similar transaction, we will notify you in
          advance and you may delete your account before the transfer takes effect if you do not
          wish your data to transfer to the new entity.
        </p>
      </section>

      <section id="terms-21" className="scroll-mt-24">
        <h2 className="text-xl">21. No third-party beneficiaries</h2>
        <p>
          These Terms are for the benefit of the parties only. No third party has any right to
          enforce any provision of these Terms. In particular, a visitor whose data you submit to
          the Platform is not a party to these Terms and may not enforce them against us, although
          the visitor retains all rights under the KDPA and the Privacy Policy.
        </p>
      </section>

      <section id="terms-22" className="scroll-mt-24">
        <h2 className="text-xl">22. Waiver</h2>
        <p>
          Our failure to enforce any right or provision of these Terms is not a waiver of that right
          or provision. A waiver of any right or provision is effective only if in writing and
          signed by an authorised representative of Secure Labs Limited.
        </p>
      </section>

      <section id="terms-23" className="scroll-mt-24">
        <h2 className="text-xl">23. Anti-corruption</h2>
        <p>
          You agree that you will not, in connection with these Terms or your use of the Platform,
          offer, promise, give, request, agree to receive, or accept any financial or other
          advantage that is intended to induce or reward improper performance, in violation of the
          Anti-Corruption and Economic Crimes Act 2003 of Kenya, the Bribery Act 2016 of Kenya, or
          any other applicable anti-corruption law. You will promptly notify us if you become aware
          of any breach of this section.
        </p>
      </section>

      <section id="terms-24" className="scroll-mt-24">
        <h2 className="text-xl">24. Contact and version history</h2>
        <p>
          <strong>Secure Labs Limited</strong>
          <br />
          P.O. Box 8456-00100, Tom Mboya Street, Nairobi, Kenya
          <br />
          Telephone: +254 745 424 949
          <br />
          Email:{' '}
          <a className="underline" href="mailto:privacy@securelabsystems.com">
            privacy@securelabsystems.com
          </a>
        </p>
        <p>
          For data protection matters, including data subject access requests and complaints, see
          the contact details set out in the{' '}
          <a className="underline" href="/privacy">
            Privacy Policy
          </a>
          . You may also file a complaint directly with the Office of the Data Protection
          Commissioner of Kenya (
          <a
            className="underline"
            href="https://www.odpc.go.ke"
            rel="noopener noreferrer"
            target="_blank"
          >
            odpc.go.ke
          </a>
          ).
        </p>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--border-default)]">
              <th className="py-2 text-left">Version</th>
              <th className="py-2 text-left">Effective date</th>
              <th className="py-2 text-left">Summary of changes</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-2">{TERMS_OF_SERVICE_VERSION}</td>
              <td className="py-2">5 May 2026</td>
              <td className="py-2">First public version.</td>
            </tr>
          </tbody>
        </table>
      </section>
    </LegalPageShell>
  );
}
